import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
  Key as KeyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { twoFactorService, TwoFactorSetup } from '../services/twoFactorService';
import { useAuth } from '../context/AuthContext';

interface TwoFactorSetupProps {
  open: boolean;
  onClose: () => void;
  onSetupComplete: () => void;
}

const TwoFactorSetupComponent: React.FC<TwoFactorSetupProps> = ({
  open,
  onClose,
  onSetupComplete,
}) => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');

  const steps = [
    'Generate Secret',
    'Scan QR Code',
    'Verify Setup',
  ];

  const generateSecret = useCallback(async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      setError('');
      const data = await twoFactorService.generateSecret(user.email);
      setSetupData(data);
      setActiveStep(1);
    } catch (err) {
      setError('Failed to generate 2FA secret. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (open && activeStep === 0) {
      generateSecret();
    }
  }, [open, activeStep, generateSecret]);

  const handleVerifyCode = async () => {
    if (!setupData || !verificationCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 2FA Setup Debug - Starting verification...');
      console.log('User ID:', user?.id);
      console.log('Setup Data:', setupData);
      console.log('Verification Code:', verificationCode);

      // Verify the token
      console.log('🔍 Verifying token...');
      const isValid = twoFactorService.verifyTokenSync(setupData.secret, verificationCode);
      console.log('Token valid:', isValid);
      
      if (!isValid) {
        console.log('❌ Token verification failed');
        setError('Invalid verification code. Please try again.');
        return;
      }

      // Enable 2FA for the user
      console.log('🔍 Enabling 2FA for user...');
      
      if (!user?.id) {
        console.log('❌ User ID not available');
        setError('User authentication error. Please log out and log back in.');
        return;
      }
      
      const success = await twoFactorService.enable2FA(user.id, setupData.secret);
      console.log('2FA enable result:', success);
      
      if (!success) {
        console.log('❌ 2FA enable failed');
        setError('Failed to enable 2FA. Please check the browser console for details and contact your administrator if the issue persists.');
        return;
      }

      console.log('✅ 2FA enabled successfully');
      setSuccess('2FA has been successfully enabled!');
      setActiveStep(2);
      
      // Call the completion callback after a short delay
      setTimeout(() => {
        onSetupComplete();
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('❌ 2FA Setup Error:', err);
      setError(`An error occurred while setting up 2FA: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSetupData(null);
    setVerificationCode('');
    setError('');
    setSuccess('');
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Generating 2FA Secret...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please wait while we set up your authentication.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QrCodeIcon color="primary" />
              Scan QR Code
            </Typography>
            
            {setupData && (
              <Box>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center', mb: 3 }}>
                  <img 
                    src={setupData.qrCodeUrl} 
                    alt="2FA QR Code" 
                    style={{ maxWidth: '200px', height: 'auto' }}
                  />
                </Paper>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Instructions:</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    1. Open your authenticator app (Google Authenticator, Authy, etc.)<br/>
                    2. Scan the QR code above<br/>
                    3. Enter the 6-digit code from your app below
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Can't scan? Enter this key manually:
                </Typography>
                <Paper elevation={1} sx={{ p: 1, bgcolor: 'grey.100', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {setupData.manualEntryKey}
                </Paper>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" color="success.main">
              2FA Successfully Enabled!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your account is now protected with two-factor authentication.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Set Up Two-Factor Authentication
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {renderStepContent()}

        {activeStep === 1 && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              inputProps={{ 
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5rem' }
              }}
              helperText="Enter the 6-digit code from your authenticator app"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeStep < 2 && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {activeStep === 1 && (
              <Button
                onClick={handleVerifyCode}
                variant="contained"
                disabled={loading || verificationCode.length !== 6}
                startIcon={loading ? <CircularProgress size={20} /> : <KeyIcon />}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            )}
          </>
        )}
        {activeStep === 2 && (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorSetupComponent;